import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { KpiValue } from '@teamsystem-impact/shared';
import type { KpiDefinition, KpiSectionDef, KpiSubsectionDef } from '../kpiConfig';
import { KpiCard } from './KpiCard';

interface KpiSectionProps {
  section: KpiSectionDef;
  values: Map<string, KpiValue>;
  onKpiClick: (code: string, anchorEl: HTMLElement) => void;
}

interface KpiGridProps {
  kpis: KpiDefinition[];
  values: Map<string, KpiValue>;
  onKpiClick: (code: string, anchorEl: HTMLElement) => void;
}

function KpiGrid({ kpis, values, onKpiClick }: KpiGridProps) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${kpis.length}, 1fr)` }}>
      {kpis.map((def) => (
        <KpiCard
          key={def.code}
          definition={def}
          value={values.get(def.code) ?? null}
          onClick={(el) => onKpiClick(def.code, el)}
        />
      ))}
    </Box>
  );
}

function SubsectionBlock({
  subsection,
  values,
  onKpiClick,
}: {
  subsection: KpiSubsectionDef;
  values: Map<string, KpiValue>;
  onKpiClick: (code: string, anchorEl: HTMLElement) => void;
}) {
  const { t } = useTranslation();
  const label = subsection.labelKey ? t(subsection.labelKey, { defaultValue: subsection.label }) : subsection.label;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          px: 2,
          py: 0.75,
          color: '#1565c0',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          bgcolor: '#f5faff',
        }}
      >
        {label}
      </Typography>
      <KpiGrid kpis={subsection.kpis} values={values} onKpiClick={onKpiClick} />
    </Box>
  );
}

/** Raggruppa le sottosezioni in righe in base al flag `inlineWithPrevious`. */
function groupSubsectionsIntoRows(subsections: KpiSubsectionDef[]): KpiSubsectionDef[][] {
  const rows: KpiSubsectionDef[][] = [];
  for (const sub of subsections) {
    if (sub.inlineWithPrevious && rows.length > 0) {
      rows[rows.length - 1].push(sub);
    } else {
      rows.push([sub]);
    }
  }
  return rows;
}

function SubsectionRow({
  row,
  values,
  onKpiClick,
  isLast,
}: {
  row: KpiSubsectionDef[];
  values: Map<string, KpiValue>;
  onKpiClick: (code: string, anchorEl: HTMLElement) => void;
  isLast: boolean;
}) {
  if (row.length === 1) {
    return (
      <Box sx={{ borderBottom: isLast ? 'none' : '1px solid #f0f0f0' }}>
        <SubsectionBlock subsection={row[0]} values={values} onKpiClick={onKpiClick} />
      </Box>
    );
  }
  // Più sottosezioni sulla stessa riga: colonne proporzionali al numero di KPI.
  const template = row.map((s) => `${s.kpis.length}fr`).join(' ');
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: template,
        borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
      }}
    >
      {row.map((sub, idx) => (
        <Box
          key={sub.id}
          sx={{
            borderRight: idx < row.length - 1 ? '1px solid #f0f0f0' : 'none',
          }}
        >
          <SubsectionBlock subsection={sub} values={values} onKpiClick={onKpiClick} />
        </Box>
      ))}
    </Box>
  );
}

export function KpiSection({ section, values, onKpiClick }: KpiSectionProps) {
  const { t } = useTranslation();
  const hasSubsections = !!section.subsections && section.subsections.length > 0;
  const label = section.labelKey ? t(section.labelKey, { defaultValue: section.label }) : section.label;
  const rows = hasSubsections ? groupSubsectionsIntoRows(section.subsections!) : [];

  return (
    <Paper sx={{ borderRadius: 2, overflow: 'hidden', mb: 1.5 }}>
      <Box sx={{ bgcolor: section.color, color: 'white', px: 2, py: 1, fontSize: 13, fontWeight: 600 }}>
        {section.icon} <span>{label}</span>
      </Box>
      {hasSubsections
        ? rows.map((row, idx) => (
            <SubsectionRow
              key={row.map((s) => s.id).join('+')}
              row={row}
              values={values}
              onKpiClick={onKpiClick}
              isLast={idx === rows.length - 1}
            />
          ))
        : section.kpis && (
            <KpiGrid kpis={section.kpis} values={values} onKpiClick={onKpiClick} />
          )}
    </Paper>
  );
}
